/**
 * Phase 7.6 — OperationalChip.
 *
 * Renders the attention level of a guidance entry as a calm chip:
 *   - informational     → emerald  ("Just for awareness")
 *   - review_suggested  → blue     ("Worth reviewing")
 *   - no_action_needed  → emerald  ("All steady")
 *   - safe_to_explore   → emerald  ("Safe to explore")
 *
 * The chip's job is to answer the operator's third emotional
 * question — "what level of attention is appropriate?" — at a glance,
 * without forcing them to open the full tooltip.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Info, Eye, CheckCircle2, Compass } from "lucide-react";

const STYLES = {
    informational: {
        icon: Info,
        color: "#A7F3D0",
        bg: "rgba(16,185,129,0.10)",
        border: "rgba(16,185,129,0.25)",
        labelKey: "ob.guidance.attention.informational",
    },
    review_suggested: {
        icon: Eye,
        color: "#93C5FD",
        bg: "rgba(59,130,246,0.10)",
        border: "rgba(59,130,246,0.25)",
        labelKey: "ob.guidance.attention.review_suggested",
    },
    no_action_needed: {
        icon: CheckCircle2,
        color: "#A7F3D0",
        bg: "rgba(16,185,129,0.10)",
        border: "rgba(16,185,129,0.25)",
        labelKey: "ob.guidance.attention.no_action_needed",
    },
    safe_to_explore: {
        icon: Compass,
        color: "#A7F3D0",
        bg: "rgba(16,185,129,0.08)",
        border: "rgba(16,185,129,0.22)",
        labelKey: "ob.guidance.attention.safe_to_explore",
    },
};

export const OperationalChip = ({ attention, testId }) => {
    const { t } = useTranslation();
    const cfg = STYLES[attention] || STYLES.informational;
    const Icon = cfg.icon;
    return (
        <span
            data-testid={testId}
            data-attention={attention}
            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] rounded-full px-2.5 py-1"
            style={{
                color: cfg.color,
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                fontFamily: "Outfit, sans-serif",
            }}
        >
            <Icon size={10} strokeWidth={1.75} aria-hidden="true" />
            {t(cfg.labelKey)}
        </span>
    );
};

export default OperationalChip;
